/**
 * Single place to map Firestore `artforms/{id}` (and Wikipedia fallback) plus
 * Ways of Seeing `artStyles` rows into UI sections / stepper steps.
 *
 * Art History (ArtformAboutStepper) and Ways of Seeing (ArtformDetailAccordions)
 * both consume this so copy stays aligned with post–Start Analysis artform data.
 */

function asStringArray(v) {
  if (!Array.isArray(v)) return [];
  return v.map((x) => String(x || '').trim()).filter(Boolean);
}

/**
 * Strips Firestore step CTA ("Tap to open 👆") meant for tappable cards; it reads wrong in accordions / expanded text.
 */
export function sanitizeArtformStepBody(body) {
  if (body == null) return '';
  if (typeof body !== 'string') return body;
  let s = String(body);
  let prev;
  do {
    prev = s;
    s = s
      .replace(/\s*Tap to open\s*👆\s*\.?/gi, '')
      .replace(/\s*Tap to open\s*\.?/gi, '')
      .trimEnd();
  } while (s !== prev);
  return s.trim();
}

/**
 * Normalize raw artform doc + optional style row from `artStyles`.
 * @param {object|null} artformData
 * @param {object} styleMeta
 */
export function normalizeArtformPayload(artformData, styleMeta = {}) {
  const sm = styleMeta || {};
  return {
    title: artformData?.title || sm.name || 'This style',
    era: artformData?.era ? String(artformData.era).trim() : '',
    whatIsIt: artformData?.whatIsIt ? String(artformData.whatIsIt).trim() : null,
    whyItsCool: artformData?.whyItsCool ? String(artformData.whyItsCool).trim() : null,
    kidFriendlySummary: artformData?.kidFriendlySummary
      ? String(artformData.kidFriendlySummary).trim()
      : null,
    visualClues: asStringArray(artformData?.visualClues),
    steps: Array.isArray(artformData?.steps) ? artformData.steps : [],
    artists: asStringArray(artformData?.artists),
    famousWorks: Array.isArray(artformData?.famousWorks) ? artformData.famousWorks : [],
    styleDescription: sm.description || sm.kidFriendlyDescription || null,
    styleChips: asStringArray(sm.characteristics),
    styleArtists: asStringArray(sm.famousArtists),
    yearRange: sm.yearRange ? String(sm.yearRange).trim() : null,
  };
}

/**
 * Sequential steps for ArtformAboutStepper (same bodies as accordions, minus
 * chips/period which the stepper historically omitted — we add kid fields first).
 */
export function buildStepperStepList(artformData) {
  if (!artformData) return [];

  const steps = [];

  if (artformData.whatIsIt) {
    const body = sanitizeArtformStepBody(artformData.whatIsIt);
    if (body) {
      steps.push({
        id: 'what-is-it',
        label: 'What is it?',
        body,
        type: 'custom',
      });
    }
  }
  if (artformData.whyItsCool) {
    const body = sanitizeArtformStepBody(artformData.whyItsCool);
    if (body) {
      steps.push({
        id: 'why-its-cool',
        label: "Why it's cool",
        body,
        type: 'custom',
      });
    }
  }

  if (artformData.kidFriendlySummary) {
    const body = sanitizeArtformStepBody(artformData.kidFriendlySummary);
    if (body) {
      steps.push({
        id: 'kid-friendly-summary',
        label: 'Quick read',
        body,
        type: 'custom',
      });
    }
  }

  if (artformData.visualClues && Array.isArray(artformData.visualClues) && artformData.visualClues.length > 0) {
    const lines = artformData.visualClues.map((line) => sanitizeArtformStepBody(line)).filter(Boolean);
    if (lines.length) {
      steps.push({
        id: 'visual-clues',
        label: 'Visual clues to spot',
        body: lines,
        type: 'visual-clues',
      });
    }
  }

  if (artformData.steps && Array.isArray(artformData.steps)) {
    artformData.steps.forEach((step, idx) => {
      const body = sanitizeArtformStepBody(step.body || '');
      if (!body) return;
      steps.push({
        id: step.id || `step-${steps.length + 1}`,
        label: step.label || 'Step',
        body,
        type: 'custom',
      });
    });
  }

  if (artformData.artists && Array.isArray(artformData.artists) && artformData.artists.length > 0) {
    steps.push({
      id: 'artists',
      label: 'Artists to Know',
      body: artformData.artists,
      type: 'artists',
    });
  }

  if (artformData.famousWorks && Array.isArray(artformData.famousWorks) && artformData.famousWorks.length > 0) {
    steps.push({
      id: 'famous-works',
      label: 'Famous Works',
      body: artformData.famousWorks,
      type: 'famous-works',
    });
  }

  return steps;
}

/**
 * Expandable sections for Ways of Seeing (and anywhere else we want the same facts).
 * @returns {Array<{ id: string, type: string, title: string, body?: string, items?: string[], works?: object[], parts?: { h: string, t: string }[] }>}
 */
export function buildAccordionSections(artformData, styleMeta = {}) {
  const n = normalizeArtformPayload(artformData, styleMeta);
  const sections = [];

  /** Same order and headings as post–Start Analysis movement cards (ArtformAboutStepper). */
  if (n.whatIsIt) {
    const body = sanitizeArtformStepBody(n.whatIsIt);
    if (body) {
      sections.push({
        id: 'what-is-it',
        type: 'text',
        title: 'What is it?',
        body,
      });
    }
  }
  if (n.whyItsCool) {
    const body = sanitizeArtformStepBody(n.whyItsCool);
    if (body) {
      sections.push({
        id: 'why-its-cool',
        type: 'text',
        title: 'Why is it cool?',
        body,
      });
    }
  }
  if (n.kidFriendlySummary) {
    const body = sanitizeArtformStepBody(n.kidFriendlySummary);
    if (body) {
      sections.push({
        id: 'kid-friendly-summary',
        type: 'text',
        title: 'Want a quick read?',
        body,
      });
    }
  }
  const clueItems = n.visualClues.map((line) => sanitizeArtformStepBody(line)).filter(Boolean);
  if (clueItems.length) {
    sections.push({
      id: 'visual-clues',
      type: 'list',
      title: 'What visual clues can you spot?',
      items: clueItems,
    });
  }

  if (!n.whatIsIt && !n.whyItsCool && !n.kidFriendlySummary && n.styleDescription) {
    const body = sanitizeArtformStepBody(n.styleDescription);
    if (body) {
      sections.push({
        id: 'style-fallback-desc',
        type: 'text',
        title: `What is ${n.title}?`,
        body,
      });
    }
  }

  n.steps.forEach((step, i) => {
    const body = sanitizeArtformStepBody(step.body || '');
    if (!body) return;
    const rawLabel = (step.label || '').trim();
    let title = rawLabel || `Learning step ${i + 1}`;
    if (title && !/[.!?…]$/.test(title)) {
      title = `${title}?`;
    }
    sections.push({
      id: step.id || `learning-step-${i}`,
      type: 'text',
      title,
      body,
    });
  });

  const artistList = n.artists.length ? n.artists : n.styleArtists;
  if (artistList.length) {
    sections.push({
      id: 'artists',
      type: 'list',
      title: 'Which artists should you know?',
      items: artistList,
    });
  }

  if (n.famousWorks.length) {
    sections.push({
      id: 'famous-works',
      type: 'works',
      title: 'What famous works can you explore?',
      works: n.famousWorks,
    });
  }

  if (n.styleChips.length) {
    sections.push({
      id: 'characteristics',
      type: 'chips',
      title: 'What are key characteristics?',
      items: n.styleChips,
    });
  }

  const periodBits = [n.era, n.yearRange].filter(Boolean);
  if (periodBits.length) {
    sections.push({
      id: 'time-period',
      type: 'text',
      title: 'When was it?',
      body: periodBits.join(' · '),
    });
  }

  if (!sections.length) {
    sections.push({
      id: 'placeholder',
      type: 'text',
      title: 'What can you learn about this style?',
      body: "We're still gathering friendly facts for this style. Check back soon, or explore Learn Art History for deeper dives on famous artworks!",
    });
  }

  return sections;
}
